import React, { useRef, useState, useEffect } from 'react';
import { useDrag } from '@use-gesture/react';
import { useSprings, animated, to } from 'react-spring';
import swap from 'lodash-move';
import EventsTicketsList from './TicketsPage/EventsTicketsList';


const TicketsPageTemplate = ({
  activeLanguage, handlerSetComponents, components, id, reusableComponent, reusableComponentSlugname, languages,
}) => {
  const rowHeight = 50;
  const styles = {};

  const clamp = (num, clamp, higher) => (higher ? Math.min(Math.max(num, clamp), higher) : Math.min(num, clamp));

  const fn = (order, down, originalIndex, currIndex, y) => ind => (down && ind === originalIndex
    ? {
      y: currIndex * rowHeight + y,
      scale: 1,
      zIndex: '1',
      shadow: 15,
      immediate: n => n === 'y' || n === 'zIndex',
    }
    : {
      y: order.indexOf(ind) * rowHeight,
      scale: 1,
      zIndex: '0',
      shadow: 1,
      immediate: false,
    });

  const initialState = {
    type: 'CollapsibleList',
    color: 'white',
    components: [],
  };

  const eventInitialState = {
    text: {
      en: '',
      es: '',
      ca: '',
      pt: '',
    },
    button: {
      category: '',
      text: {
        en: '',
        es: '',
        ca: '',
        pt: '',
      },
    },
    content: [
      {
        type: 'CarouselTickets',
        eventSlugnames: [],
      },
    ],
  };

  const [state, setState] = useState(initialState);
  const [selected, setSelected] = useState([]);
  const [order, setOrder] = useState([], [selected]);
  const [index, setIndex] = useState();

  // Create springs, each corresponds to an item, controlling its transform, scale, etc.
  const [springs, springApi] = useSprings(selected.length, fn(order), [selected, order]);
  // Preps a gesture handler which returns drag-deltas, touched/clicked state, etc.
  const bind = useDrag(({ args: [originalIndex], down, movement: [, y] }) => {
    // Bunch of math to calculate current row and new order,
    const curIndex = order.indexOf(originalIndex);
    const curRow = clamp(Math.round((curIndex * rowHeight + y) / rowHeight), 0, selected.length - 1);
    const newOrder = swap(order, curIndex, curRow);

    springApi.start(fn(newOrder, down, originalIndex, curIndex, y));

    if (!down) {
      setOrder(newOrder);

      // reorder selected and update component on PostContainer
      const newComponentsOrder = newOrder.map(index => selected[index]);

      const newState = {
        ...state,
        components: newComponentsOrder,
      };
      setState(newState);
      const componentObj = {
        id,
        component: newState,
      };
      handlerSetComponents(componentObj, index);
    }
  });

  const updateComponentInParentState = (comp, compIndex) => {
    const newSelected = selected;

    newSelected[compIndex] = comp;

    const newComponentsOrder = order.map(index => newSelected[index]);

    const newState = {
      ...state,
      components: newComponentsOrder,
    };
    setState(newState);
    const componentObj = {
      id,
      component: newState,
    };
    handlerSetComponents(componentObj, index);
  };


  const deleteItemHandler = (itemIndex) => {
    const newArr = selected;
    newArr.splice(itemIndex, 1);
    setSelected(newArr);
    const ii = order.findIndex(num => num === itemIndex);
    const n = [...order.slice(0, ii), ...order.slice(ii + 1).map(num => (num > 0 && num > ii ? num - 1 : num))];
    setOrder(n);
  };


  const addItemHandler = () => {
    setSelected([...selected, eventInitialState]);
    setOrder([...order, order.length]);
  };

  useEffect(() => {
    const componentIndex = components.findIndex(obj => obj.id === id);
    setIndex(componentIndex);
    const { component } = components[componentIndex];
    if (reusableComponent) {
      setState(reusableComponent);
      if (!component) handlerSetComponents({ component: { key: reusableComponentSlugname } }, componentIndex);
    } else {
      // setIndex(componentIndex);

      if (component) {
        setSelected(component.components);
        setState(component);
        setOrder(component.components.map((_, i) => i));
        // order.current = selected.map((_, index) => index);
      } else {
        const componentObj = {
          id,
          component: state,
        };
        handlerSetComponents(componentObj, componentIndex);
      }
    }
  }, []);


  return (
    <div className="content" style={{ height: (selected.length * rowHeight) + 30 }}>
      <button type="button" onClick={addItemHandler} className="font-americaMonoBold text-xs pl-10 pr-6 py-1 h-7 ml-12 my-1 rounded-full uppercase bg-greyFilter flex flex-row items-center">
add new
        <span className="font-icon icon-add-button text-base font-bold pl-2" />
      </button>
      {springs.map(({
        zIndex, shadow, y, scale,
      }, i) => (
        <animated.div
          {...bind(i)}
          key={i}
          style={{
            zIndex,
            // @use-gesture 10 needs this or the browser scrolls instead of dragging.
            touchAction: 'none',
            boxShadow: shadow.to(s => `rgba(0, 0, 0, 0.15) 0px ${s}px ${2 * s}px 0px`),
            transform: to([y], (y, s) => `translate3d(0,${y}px,0)`),
          }}
        >
          <EventsTicketsList languages={languages} activeLanguage={activeLanguage} event={selected[i]} index={i} deleteItemHandler={deleteItemHandler} updateComponentInParentState={updateComponentInParentState} />
        </animated.div>
      ))}
    </div>
  );
};
export default TicketsPageTemplate;
